import { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import Icon from '@components/Icon';
import { Radio, Switch, Input, Slider, InputNumber, Select } from 'antd';
import ColorPicker from '@components/ColorPicker';
import stores from '@stores';
import { text2Svg } from '@utils/utils';

// 常用字体选项（标签将在组件内通过翻译获取）
const fontOptionsConfig = [
    { value: 'sans-serif', labelKey: 'watermark.fonts.sansSerif' },
    { value: 'serif', labelKey: 'watermark.fonts.serif' },
    { value: 'cursive', labelKey: 'watermark.fonts.cursive' },
    { value: 'fantasy', labelKey: 'watermark.fonts.fantasy' },
    { value: 'monospace', labelKey: 'watermark.fonts.monospace' },
    { value: 'Arial, sans-serif', labelKey: 'watermark.fonts.arial' },
    { value: 'Verdana, sans-serif', labelKey: 'watermark.fonts.verdana' },
    { value: 'Georgia, serif', labelKey: 'watermark.fonts.georgia' },
    { value: 'Comic Sans MS, cursive', labelKey: 'watermark.fonts.comicSans' },
    { value: 'Pacifico, cursive', labelKey: 'watermark.fonts.pacifico' }
];

export default observer(() => {
    const { t, i18n } = useTranslation('toolbar');
    const [useWater, setUseWater] = useState(false);
    const [waterCont, setWaterCont] = useState('');
    const [waterColor, setWaterColor] = useState('#00000030');
    const [direction, setDirection] = useState(45);
    const [fontSize, setFontSize] = useState(36);
    const [fontFamily, setFontFamily] = useState('sans-serif');
    
    // 初始化时设置默认水印文本，语言切换时如果内容为空或为默认值则更新
    useEffect(() => {
        const defaultText = t('watermark.defaultText');
        if (!waterCont || waterCont === '图美啦' || waterCont === 'SnapBeautify') {
            setWaterCont(defaultText);
        }
    }, [i18n.language]);
    
    const handleColorChange = (color) => {
        setWaterColor(typeof color === 'string' ? color : color.toRgbString());
    };
    
    useEffect(() => {
        if (useWater && waterCont.trim()) {
            const svgImg = text2Svg({
                text: waterCont,
                color: waterColor,
                angleDegrees: direction,
                fontSize: fontSize,
                fontFamily: fontFamily
            });
            stores.option.setWaterImg(svgImg);
        } else {
            stores.option.setWaterImg(null);
        }
    }, [useWater, waterCont, waterColor, direction, fontSize, fontFamily]);
    
    return (
        <div>
            <div className="[&_label]:font-semibold [&_label]:text-sm flex gap-4 items-center justify-between">
                <label className="flex items-center font-medium">
                    {t('watermark.enable')}
                </label>
                <Switch 
                    checked={useWater} 
                    onChange={setUseWater} 
                    size="small" 
                    className="bg-slate-200" 
                />
            </div>
            {useWater &&
                <div className="[&_label]:font-semibold [&_label]:text-xs grid gap-3 mt-3">
                    <Input 
                        value={waterCont} 
                        placeholder={t('watermark.content')} 
                        onChange={(e) => setWaterCont(e.target.value)} 
                    />
                    
                    {/* 字体选择 */}
                    <div className="flex items-center justify-between">
                        <label className="flex items-center">
                            <Icon.Type size={14} className="mr-1 text-purple-500 dark:text-purple-400" />
                            {t('watermark.fontStyle')}
                        </label>
                        <Select
                            size="small"
                            style={{ width: 120 }}
                            value={fontFamily}
                            onChange={setFontFamily}
                            options={fontOptionsConfig.map(font => ({
                                value: font.value,
                                label: t(font.labelKey)
                            }))}
                        />
                    </div>
                    
                    {/* 字体大小 */}
                    <div>
                        <div className="flex items-center justify-between mb-1">
                            <label className="flex items-center">
                                <Icon.Type size={14} className="mr-1 text-purple-500 dark:text-purple-400" />
                                {t('watermark.fontSize')}
                            </label>
                            <InputNumber
                                min={12}
                                max={100}
                                value={fontSize}
                                onChange={setFontSize}
                                size="small"
                                style={{ width: 70 }}
                                addonAfter="px"
                            />
                        </div>
                        <Slider
                            min={12}
                            max={100}
                            onChange={setFontSize}
                            value={fontSize}
                            step={1}
                            tooltip={{ formatter: (value) => `${value}px` }}
                        />
                    </div>
                    
                    <div className="flex items-center justify-between">
                        <label className="flex items-center">
                            <Icon.Pipette size={14} className="mr-1 text-purple-500 dark:text-purple-400" />
                            {t('watermark.color')}
                        </label>
                        <ColorPicker value={waterColor} onChange={handleColorChange} size="small" />
                    </div>
                    
                    <div>
                        <div className="flex items-center justify-between mb-1">
                            <label className="flex items-center">
                                <Icon.RotateCcw size={14} className="mr-1 text-purple-500 dark:text-purple-400" />
                                {t('watermark.rotation')}
                            </label>
                            <InputNumber
                                min={-180}
                                max={180}
                                value={direction}
                                onChange={setDirection}
                                size="small"
                                style={{ width: 70 }}
                                addonAfter="°"
                            />
                        </div>
                        <Slider
                            min={-180}
                            max={180}
                            onChange={setDirection}
                            value={direction}
                            step={5}
                            tooltip={{ formatter: (value) => `${value}°` }}
                        />
                    </div>
                    
                    <div className="flex items-center justify-between">
                        <label className="flex items-center">
                            <Icon.Box size={14} className="mr-1 text-purple-500 dark:text-purple-400" />
                            {t('watermark.backgroundOnly')}
                        </label>
                        <Switch size="small" onChange={(checked) => stores.option.setWaterIndex(checked ? -1 : 1)} className="bg-slate-200" />
                    </div>
                </div>
            }
        </div>
    )
});